/**
 * Reine Umrechnungs-Helfer fuer die Risiko-Scatter-Ansicht (P-08).
 * Bewusst frei von React/DOM, damit Position<->Wert-Logik isoliert testbar ist.
 *
 * WICHTIG: `score` ist im Backend eine GENERATED COLUMN (weight × assessment) und
 * darf beim PATCH niemals mitgesendet werden — nur weight/assessment sind schreibbar.
 * `buildMovePatch` liefert deshalb ausschliesslich diese beiden Felder.
 */

export const LEVELS = [1, 2, 3, 4, 5] as const;
export const MIN_LEVEL = 1;
export const MAX_LEVEL = 5;

/** Rundet auf ganzzahlige Stufe und klemmt auf den gueltigen Bereich 1-5. */
export function clampLevel(n: number): number {
  if (Number.isNaN(n)) return MIN_LEVEL;
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(n)));
}

/**
 * Rechnet eine Pixel-Position innerhalb einer Achse (0..trackSize, aufsteigend)
 * in eine Stufe 1-5 um. Stufe n belegt das Intervall [(n-1)/5, n/5) * trackSize.
 */
export function pixelToLevel(px: number, trackSize: number): number {
  if (trackSize <= 0) return MIN_LEVEL;
  const ratio = px / trackSize;
  const raw = ratio * 5 + 0.5;
  return clampLevel(raw);
}

/** Rechnet eine Stufe 1-5 in die Pixel-Mitte ihres Zellintervalls um (aufsteigende Achse). */
export function levelToPixel(level: number, trackSize: number): number {
  const l = clampLevel(level);
  return ((l - 0.5) / 5) * trackSize;
}

export interface JitterInput {
  index: number;
  total: number;
}

export interface JitterOffset {
  dx: number;
  dy: number;
}

/**
 * Deterministischer kleiner Versatz fuer mehrere Risiken auf derselben Zelle,
 * damit sie im Scatter nicht exakt uebereinanderliegen. Bei genau einem Element
 * (kein Overlap) ist der Versatz {0,0}.
 */
export function jitterOffset({ index, total }: JitterInput, spread = 10): JitterOffset {
  if (total <= 1) return { dx: 0, dy: 0 };
  const angle = (index / total) * Math.PI * 2;
  const radius = spread * (0.55 + 0.45 * ((index % 3) / 2));
  return { dx: Math.cos(angle) * radius, dy: Math.sin(angle) * radius };
}

export interface RiskMovePatch {
  weight: number;
  assessment: number;
}

/**
 * Baut das PATCH-Payload fuer eine per Drag verschobene Risiko-Position.
 * Enthaelt AUSSCHLIESSLICH weight + assessment (auf 1-5 geklemmt) — niemals `score`.
 */
export function buildMovePatch(weight: number, assessment: number): RiskMovePatch {
  return { weight: clampLevel(weight), assessment: clampLevel(assessment) };
}
